/**
 * The 7 Brand Boosters — marketer-facing curation over our 10 latent elements.
 *
 * Decision (SYR-68): presentation-only curation. The engineering model stays
 * at 10 LEs; this list picks 7 and labels two of them as "indirect" — they
 * feed Brand Equity through another LE but matter enough to a marketer to
 * surface alongside the direct drivers.
 *
 * `pathStrength` is a 0..1 normalised contribution to Brand Equity, derived
 * from the LE weight chain in `brand-equity-api/src/brand-equity/latent-elements.config.ts`.
 * Used for orbital line widths and Q1 ordering hints.
 *
 * Indirect contribution chains (computed from the model):
 *   Brand Loyalty       direct          0.30
 *   Perceived Quality   direct + viaBL  0.30 + 0.20×0.30 = 0.360
 *   WTPP                direct + viaBL  0.20 + 0.45×0.35×0.30 = 0.247
 *   Associations        direct + viaBL  0.10 + 0.05×0.30 = 0.115
 *   Market Awareness    direct          0.20
 *   Brand Attachment    via WTPP        0.50 × 0.247 = 0.123
 *   Alignment Depth     via BehL→BL     0.30 × 0.35 × 0.30 = 0.0315
 *
 * Normalising to PQ=1.0:
 *   PQ 1.00, BL 0.83, MA 0.56, WTPP 0.69, Brand Attachment 0.34,
 *   Associations 0.32, Alignment Depth 0.087
 */

export interface BoosterConfig {
  /** Stable presentation key. */
  key: string;
  /** Canonical name — match against API latent-element response. */
  canonical: string;
  /** Marketer axis label. */
  axis: string;
  /** Brief description of what this booster captures. */
  meaning: string;
  /** Path-strength label rendered next to each booster card. */
  pathLabel: string;
  /** True if this LE doesn't directly feed Brand Equity. */
  indirect: boolean;
  /** 0..1 contribution to BE, normalised to the strongest direct path. */
  pathStrength: number;
}

export const BOOSTERS: readonly BoosterConfig[] = Object.freeze([
  {
    key: 'loyalty',
    canonical: 'Brand Loyalty',
    axis: 'Retention',
    meaning:
      'Repeat behaviour plus the attitude behind it. By far the strongest path to equity.',
    pathLabel: 'dominant path',
    indirect: false,
    pathStrength: 0.83,
  },
  {
    key: 'pq',
    canonical: 'Perceived Quality',
    axis: 'Product',
    meaning:
      'How customers feel about what the brand actually sells, whether it is seen as superior, reliable and worth it.',
    pathLabel: 'direct path',
    indirect: false,
    pathStrength: 1.0,
  },
  {
    key: 'wtpp',
    canonical: 'Willingness to Pay a Premium',
    axis: 'Pricing power',
    meaning:
      'Appetite to pay more for your brand than for alternatives, both a reflection of equity and a reinforcer of it.',
    pathLabel: 'direct path',
    indirect: false,
    pathStrength: 0.69,
  },
  {
    key: 'assoc',
    canonical: 'Associations',
    axis: 'Meaning',
    meaning:
      'How clear, distinctive and memorable the brand’s mental picture is.',
    pathLabel: 'direct path',
    indirect: false,
    pathStrength: 0.32,
  },
  {
    key: 'attach',
    canonical: 'Brand Attachment',
    axis: 'Emotion',
    meaning:
      'The emotional bond: how much the brand is part of who someone is.',
    pathLabel: 'indirect, via Willingness to Pay',
    indirect: true,
    pathStrength: 0.34,
  },
  {
    key: 'touch',
    canonical: 'Alignment Depth', // displayed as "Touchpoints" via getDisplayName
    axis: 'Experience',
    meaning:
      'Whether the brand shows up consistently and relevantly across the spaces customers live in.',
    pathLabel: 'indirect, via Brand Loyalty',
    indirect: true,
    pathStrength: 0.087,
  },
  {
    key: 'awareness',
    canonical: 'Market Awareness',
    axis: 'Salience',
    meaning:
      'Awareness of the brand in the open market, beyond past customers: the only measure not based on existing customers.',
    pathLabel: 'direct path',
    indirect: false,
    pathStrength: 0.56,
  },
]);

/** Lookup by stable key. */
export function getBoosterConfig(key: string): BoosterConfig | undefined {
  return BOOSTERS.find((b) => b.key === key);
}
